import React, {useState } from 'react'
import { graphql, Link } from 'gatsby'
import { GatsbyImage } from "gatsby-plugin-image";

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleDown } from '@fortawesome/free-solid-svg-icons/faAngleDown'
import { faAngleRight } from '@fortawesome/free-solid-svg-icons/faAngleRight'
import { faAngleUp } from '@fortawesome/free-solid-svg-icons/faAngleUp'

import Breadcrumb from '../components/Breadcrumb'
import HTMLContent from '../components/Content'
import Layout from '../components/Layout'
import LeadCapture from '../components/LeadCapture'
import BgImage from '../components/BgImage'
import FixedCtaDisplay from '../components/FixedCtaDisplay'
import OfferPopup from '../components/OfferPopup'

import ScrollHandler from '../components/ScrollHandler'

import SEO from '../components/Seo'
import { wordToLink } from '../utils/helpers'

const getSlugFunction = require('../utils/slugify')

const PageTemplate = ({
  title,
  content,
  location,
  diag,
  healthPrime,
  banner,
}) => {
  const [showToc, setShowToc] = useState(false)
  const service = 'Lab-Sample Collection'
  const leadFormCta = 'Book Now'
  const leadFormHeading = 'Home Pickup'

  const {
    name,
    aliasname,
    metadescription,
    sections,
    related_tests,
    test_references,
    porteaLabId,
    keyword_link,
    conclusion,
    Disclaimer,
    test_external_reference_links,
  } = content

  const diagImg = diag.childImageSharp
  const primeImg = healthPrime.childImageSharp

  const relatedTestsByName = related_tests.reduce((group, test) => {
    group[test.name] = group[test.name] || test
    return group
  }, {})

  const uniqueRelatedTests = Object.values(relatedTestsByName)

  const breadcrumbs = [
    { target: '/', display: 'Home', keep: true },
    { target: '/diagnostic', display: 'Diagnostics', keep: true },
    { target: '/labs/diagnostic-tests', display: 'Lab Tests', keep: true },
    { target: location, display: name },
  ]

  const bgImage = banner.childImageSharp.gatsbyImageData

  const processed = []

  sections.forEach(section => {
    const value = section.description
    section.description_with_link = value
    let returnValue = null
    keyword_link.forEach(({ link, keyword }) => {
      // find if keyword exist  in   processed array
      if (processed.indexOf(keyword) == -1) {
        returnValue = wordToLink(value, keyword, link)
        if (returnValue != null) {
          section.description_with_link = returnValue
          processed.push(keyword)
        }
      }
    })
  })

  const testAppended = name.toLowerCase().endsWith('test');

  const toc = sections.map((s, i) => ({
    href: `#section_${i}`,
    title: s.heading,
  }))

  if (test_references.length > 0) {
    toc.push({ href: '#test_references', title: 'Understanding Results' })
  }

  if (conclusion && conclusion.trim().length) {
    toc.push({ href: '#conclusion', title: 'Conclusion' })
  }

  if (
    test_external_reference_links &&
    test_external_reference_links.length > 0
  ) {
    toc.push({ href: '#external_links', title: 'References' })
  }

  return (
    <React.Fragment>
      <SEO
        title={title || `${name} Lab Test | ${aliasname} Lab Test| Portea`}
        description={
          metadescription ||
          `Get tested for ${name} at home with Portea. We are the most affordable, accountable and accessible home healthcare provider for various services like ${name} at home. Doorstep sample collection`
        }
        url={location}
        service={service}
        breadcrumbs={breadcrumbs}
      />
      <Breadcrumb breadcrumbs={breadcrumbs} />

      <ScrollHandler
        render={() => (
          <FixedCtaDisplay
            service={service}
            location={location}
            cta={leadFormCta}
            heading={leadFormHeading}
          />
        )}
      />
      <OfferPopup category='Diagnostic'/>

      <section className="hero">
        {bgImage && <BgImage alt="banner" image={bgImage} height="100%" />}

        <div className="hero-body">
          <div className="container is-fluid">
            <div className="columns">
              <div className="column is-12">
                <div className=" level">
                  <div className="level-left">
                    <div>
                      <h1 className="title has-text-weight-bold is-bordered-text capitalize">
                        {`${name.toLowerCase()}${testAppended ? '' : ' Test'}`}
                      </h1>
                      <h2 className="subtitle">{aliasname}</h2>
                    </div>
                  </div>

                  <div className="level-right">
                    <div className="leadCtr level-item">
                      <LeadCapture
                        service={service}
                        location={location}
                        cta={leadFormCta}
                        heading={leadFormHeading}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="section">
        <div className="container is-fluid">
          <div className="columns">
            <div className="column is-9">
              {toc.length > 0 && (
                <aside className="menu">
                  <div className='menu-header'>
                  <p className="menu-label">Table of Contents</p>
                    <div className="icon-action">
                      <FontAwesomeIcon
                        icon={showToc ? faAngleUp : faAngleDown}
                        color="black"
                        className="menu-icon"
                        onClick={() => setShowToc(!showToc)}
                      />
                    </div>
                  </div>
                  <ul className={`menu-list ${showToc && 'is-hidden'}`}>
                    {toc.map(({ href, title: linkTitle }) => (
                      <li key={href}>
                        <a href={href}>
                          <span>{linkTitle}</span>
                          <FontAwesomeIcon
                            icon={faAngleRight}
                            color="black"
                            className="menu-icon"
                          />
                        </a>
                      </li>
                    ))}
                  </ul>
                </aside>
              )}

              <div className="content">
                {sections &&
                  sections.map((c, i) => (
                    <>
                      <div key={`section_${i}`} className="content-section" id={`section_${i}`}>
                        <h2>{c.heading.toLowerCase()}</h2>

                        <HTMLContent content={c.description_with_link} />
                      </div>
                      <>
                        {i === 1 && (
                          <a href="https://lp.portea.com/diagno-offerings/2023/?utm_source=banner-image&utm_medium=organic&utm_campaign=image2LP&utm_content=traffic_organic_banner2LP">
                            <GatsbyImage alt="" image={diagImg.gatsbyImageData} />
                          </a>
                        )}
                      </>
                    </>
                  ))}

                {test_references.length > 0 && (
                    <div className="content-section" id="test_references">
                      <h2>
Understanding results of
                        {name}
                      </h2>

                      <table className="table table-bordered">
                        <thead className="grid-header">
                          <tr>
                            <th>Reference Range</th>
                            <th>Interpretation</th>
                          </tr>
                        </thead>
                        <tbody>
                          {test_references.map((tref, i) => (
                            <tr key={i}>
                              <td>{tref.Referencerange}</td>
                              <td>{tref.Interpretation}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      <small>
                        <p dangerouslySetInnerHTML={{ __html: Disclaimer }} />
                      </small>
                    </div>
                )}

                {conclusion &&
                  conclusion.trim().length && (
                      <div className="content-section" id="conclusion">
                        <h2>Conclusion</h2>

                        <HTMLContent content={conclusion} />
                      </div>
                  )}

                {test_external_reference_links &&
                  test_external_reference_links.length > 0 && (
                      <div className="content-section" id="external_links">
                        <h2>References</h2>
                        <ol>
                          {test_external_reference_links.map((exref, i) => (
                            <li className="wrap-content" key={exref.link}>
                              <a href={exref.link} target="_blank">
                                {exref.link}
                              </a>
                            </li>
                          ))}
                        </ol>
                      </div>
                  )}
              </div>
            </div>

            <div className="column">
              <nav className="panel">
                <p className="panel-heading has-text-weight-bold">View More</p>

                <Link className="panel-block" to="/labs/diagnostic-tests">
                  Diagnostic Tests
                </Link>
                <Link className="panel-block" to="/labs/packages">
                  Lab Packages
                </Link>
              </nav>

              {uniqueRelatedTests.length > 0 && (
                <nav className="panel">
                  <p className="panel-heading has-text-weight-bold">
                    Related Tests
                  </p>

                  {uniqueRelatedTests.map(({ id, name: testName }) => (
                    <Link
                      key={testName}
                      className="panel-block"
                      to={getSlugFunction('PorteaLabLabTestNode')(testName, id)}
                    >
                      {testName}
                    </Link>
                  ))}
                </nav>
              )}
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

const Page = ({ data }) => {
  const { porteaLabLabTestNode: page, diag, healthPrime, banner } = data

  return (
    <Layout>
      <PageTemplate
        title={page.title}
        content={page}
        location={page.fields.slug}
        diag={diag}
        healthPrime={healthPrime}
        banner={banner}
      />
    </Layout>
  )
}

export default Page

export const pageQuery = graphql`query LabTestQuery($id: String!) {
  porteaLabLabTestNode(id: {eq: $id}) {
    id
    name
    aliasname
    porteaLabId
    related_tests {
      id
      name
    }
    Disclaimer
    conclusion
    title
    metadescription
    metakeywords
    keyword_link {
      keyword
      link
    }
    test_references {
      Referencerange
      Interpretation
    }
    sections {
      heading
      description
    }
    fields {
      slug
    }
    test_external_reference_links {
      link
    }
  }
  healthPrime: file(relativePath: {eq: "Diabetes_banner.jpg"}) {
    childImageSharp {
      gatsbyImageData(layout: FULL_WIDTH)
    }
  }
  diag: file(relativePath: {eq: "Diagnostics.png"}) {
    childImageSharp {
      gatsbyImageData(layout: FULL_WIDTH)
    }
  }
  banner: file(relativePath: {eq: "banners/diagnostic.jpg"}) {
    childImageSharp {
      gatsbyImageData(layout: FULL_WIDTH)
    }
  }
}`
